import { useInjection } from 'inversify-react';
import { cloneDeep } from 'lodash/fp';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useMemo } from 'react';
import { IAnnotationService } from '../../../annotation.service';
import { IAnnotationsStore } from '../../../annotations.store';
import { IAnnotationUiStore } from '../../../annotationUi.store';
import { IFreeDrawSegmentationService } from '../../../freeDrawSegmentation.service';
import { ChangeSegmentationWithAnswersCommand } from '../../../undoRedoCommands/changeSegmentationWithAnswersCommand';
import { IUndoRedoHistory } from '../../../undoRedoHistory.service';
import { IAnnotationTypeBl } from '../annotationType.bl';
import { IAnnotationTypeStore } from '../annotationType.store';
import { AnnotationTypeSelected } from '../components/AnnotationTypeSelected';
import { AnnotationTypesList } from '../components/AnnotationTypesList';
import { IAnnotationTypeOptions } from '../models/annotationTypeOptions';
import { AnnotationTypeHotKeysContainer } from './AnnotationTypeHotKeys.container';

interface IProps {
  readonly?: boolean;
  children?: ReactNode;
}

export const AnnotationTypesContainer = observer((props: IProps): ReactElement => {
  const annotationService = useInjection(IAnnotationService.$);
  const annotationTypeBl = useInjection(IAnnotationTypeBl.$);
  const freeDrawSegmentationService = useInjection(IFreeDrawSegmentationService.$);
  const undoRedoHistoryService = useInjection(IUndoRedoHistory.$);
  const uiStore = useInjection(IAnnotationUiStore.$);
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const annotationsStore = useInjection(IAnnotationsStore.$);

  const shouldLock = useMemo(() => {
    if (props.readonly !== undefined) {
      return props.readonly;
    }
    return annotationsStore.image === undefined && !uiStore.isImageLoading;
  }, [props.readonly, annotationsStore.image, uiStore.isImageLoading]);

  const handleAnnotationTypeSelected = async (annotationTypeId: string): Promise<void> => {
    if (await freeDrawSegmentationService.clearAsync()) {
      annotationService.deselectSegmentation();
      await annotationTypeBl.handleAnnotationTypeSelectedInAnnotationTypeContainer(annotationTypeId);
    }
  };

  const handleSelectedAnnotationDelete = async (): Promise<void> => {
    if (freeDrawSegmentationService.freeDrawFeature) {
      const segmentation = cloneDeep(annotationsStore.segmentations.find((s) => s.feature.id === freeDrawSegmentationService.freeDrawFeature?.id));

      if (segmentation?.feature) {
        annotationService.deleteSegmentation(segmentation.feature.id);
      }
      annotationService.deselectSegmentation();
      freeDrawSegmentationService.clear();
      await annotationTypeBl.handleSelectedAnnotationDeleteInAnnotationTypeContainer();

      undoRedoHistoryService.addCommand(
        new ChangeSegmentationWithAnswersCommand(annotationService, annotationsStore, freeDrawSegmentationService, segmentation, undefined)
      );
    }
  };

  const handleAnnotationTypeOptionChanged = (projectId: string, annotationTypesOptions: IAnnotationTypeOptions): void => {
    annotationTypeBl.setAnnotationTypeOptions(projectId, annotationTypesOptions);

    const featureIdsToShow = annotationsStore.segmentations
      .filter((s) => annotationTypesOptions.id === s.feature.properties?.annotationTypeId)
      .map((s) => ({ segmentationId: s.id, featureId: s.feature.id }));

    featureIdsToShow.forEach((s) => {
      annotationService.showSegmentation(s.featureId, s.segmentationId);
    });
  };

  const showList =
    (props.readonly && !annotationsStore.isSelectedSegWithQuestions) ||
    (!props.readonly &&
      !freeDrawSegmentationService.freeDrawInProgress &&
      !annotationsStore.isSelectedSegWithQuestions &&
      !annotationsStore.isSelectedSegOfFreeDrawType);

  return (
    <>
      {props.children}
      <AnnotationTypesList
        showList={showList}
        projectId={annotationsStore.projectId}
        annotationTypes={annotationTypeStore.annotationTypes.slice().sort((a, b) => a.order - b.order)}
        annotationTypesOptions={annotationTypeStore.annotationTypesOptions}
        readonly={shouldLock}
        activeAnnotationTypeId={annotationTypeStore.activeAnnotationTypeId}
        onAnnotationTypeSelected={handleAnnotationTypeSelected}
        onAnnotationTypeOptionsChanged={handleAnnotationTypeOptionChanged}
        onSelectedAnnotationDelete={handleSelectedAnnotationDelete}
        getShortcutKey={(order) => annotationTypeBl.getShortcutKey(order)}
      />
      <AnnotationTypeSelected
        showList={showList}
        projectId={annotationsStore.projectId}
        annotationTypes={annotationTypeStore.annotationTypes.slice().sort((a, b) => a.order - b.order)}
        annotationTypesOptions={annotationTypeStore.annotationTypesOptions}
        readonly={shouldLock}
        activeAnnotationTypeId={annotationTypeStore.activeAnnotationTypeId}
        onAnnotationTypeOptionsChanged={handleAnnotationTypeOptionChanged}
        onSelectedAnnotationDelete={handleSelectedAnnotationDelete}
        getShortcutKey={(order) => annotationTypeBl.getShortcutKey(order)}
      />
      {!props.readonly && <AnnotationTypeHotKeysContainer />}
    </>
  );
});
